import React, { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { Container, Row, Col, Form, Button } from 'reactstrap'
import { I18n } from 'react-redux-i18n'
import moment from 'moment'

import Modals from 'components/Modals'
import { FieldInput, FieldSelect, SelectCountry, FieldBox } from '../commons'
import { DatePicker } from 'components/DatePicker'

import { PASSENGERS } from 'constants/index'
import { conversationPassengersAct } from 'redux_new/actions'

const validateErrors = (passenger) => {
  const errors = {}

  Object.keys(PASSENGERS.passengerSchema).forEach((prop) => {
    if (!passenger[prop]) {
      errors[prop] = true
    }
  })

  return Object.keys(errors).length > 0 ? errors : false
}

const ModalPassenger = ({ showModal, onCloseModal, currentPassenger }) => {
  const [passenger, setPassenger] = useState({ ...PASSENGERS.passengerSchema })
  const [disabled, setDisabled] = useState(false)
  const [errors, setErrors] = useState({})

  const dispatch = useDispatch()

  useEffect(() => {
    setPassenger({ ...PASSENGERS.passengerSchema, ...(currentPassenger || {}) })
    setErrors({})
  }, [currentPassenger])

  const onChange = ({ target: { name, value } }) => {
    setPassenger({ ...passenger, [name]: value })
  }

  const onChangeDate = (date) => {
    setPassenger({ ...passenger, ...date })
  }

  const onDelete = async () => {
    setDisabled(true)

    try {
      await dispatch(conversationPassengersAct.deleteOne(passenger.passport))

      onCloseModal()
    } finally {
      setDisabled(false)
    }
  }

  const onSubmit = async ({ target: { name } }) => {
    setDisabled(true)

    try {
      const newErrors = validateErrors(passenger)

      if (newErrors) {
        setErrors(newErrors)
      } else {
        await dispatch(conversationPassengersAct[name](passenger))

        onCloseModal()
      }
    } finally {
      setDisabled(false)
    }
  }

  return (
    <Modals
      showClose
      isOpen={showModal}
      toggle={onCloseModal}
      header={I18n.t(!currentPassenger ? 'dashboard_add-passenger' : 'dashboard_edit-passenger')}
      body={
        <Form>
          <FieldInput
            title='passenger.name'
            name='name'
            className='pr-0'
            value={passenger.name}
            onChange={onChange}
            disabled={disabled}
            error={errors.name}
          />
          <FieldInput
            title='passenger.surnames'
            name='surnames'
            value={passenger.surnames}
            onChange={onChange}
            disabled={disabled}
            error={errors.surnames}
          />
          <FieldSelect
            title='passenger.type'
            name='type'
            options={PASSENGERS.passengerTypes}
            value={passenger.type}
            onChange={onChange}
            disabled={disabled}
            error={errors.type}
          />
          <FieldSelect
            title='passenger.gender'
            name='gender'
            options={PASSENGERS.passengerGenders}
            value={passenger.gender}
            onChange={onChange}
            disabled={disabled}
            error={errors.gender}
          />
          <FieldBox title='passenger.birthdate' error={errors.birthdate}>
            <DatePicker
              block
              id={'edit-passenger-birthdate-' + passenger.passport}
              name='birthdate'
              value={passenger.birthdate ? moment(passenger.birthdate) : null}
              handleChange={onChangeDate}
              isOutsideRange={(day) => !day.isBefore(moment().add(1, 'day'))}
              limitYear={0} //Añadimos 10 años al selector del DataPicker
              maxYears={100} //Reducimos 10 años al selector del DataPicker
            />
          </FieldBox>
          <FieldSelect
            title='passenger.documentType'
            name='documentType'
            options={PASSENGERS.passengerDocumentTypes}
            value={passenger.documentType}
            onChange={onChange}
            disabled={disabled}
            error={errors.documentType}
          />
          <FieldInput
            title='passenger.documentNumber'
            name='passport'
            value={passenger.passport}
            onChange={onChange}
            disabled={disabled}
            error={errors.passport}
          />
          <FieldBox title='passenger.expedition' error={errors.issueDate}>
            <DatePicker
              block
              id={'edit-passenger-issueDate-' + passenger.passport}
              name='issueDate'
              value={passenger.issueDate ? moment(passenger.issueDate) : null}
              handleChange={onChangeDate}
              isOutsideRange={(day) => (passenger.expirationDate ? !day.isBefore(moment(passenger.expirationDate)) : false)}
              limitYear={0} //Añadimos 10 años al selector del DataPicker
              maxYears={20} //Reducimos 10 años al selector del DataPicker
            />
          </FieldBox>
          <FieldBox title='passenger.expiration' error={errors.expirationDate}>
            <DatePicker
              block
              id={'edit-passenger-expirationDate-' + passenger.passport}
              name='expirationDate'
              value={passenger.expirationDate ? moment(passenger.expirationDate) : null}
              handleChange={onChangeDate}
              isOutsideRange={(day) => (passenger.issueDate ? !day.isAfter(moment(passenger.issueDate)) : false)}
              limitYear={20} //Añadimos 10 años al selector del DataPicker para que pueda elegir una fecha 10 años superior de la actual
            />
          </FieldBox>
          <SelectCountry
            title='passenger.countryOfResidence'
            name='countryOfResidence'
            value={passenger.countryOfResidence}
            onChange={onChange}
            disabled={disabled}
            error={errors.countryOfResidence}
          />
        </Form>
      }
      footer={
        <Container className='m-0 p-0'>
          <Row noGutters>
            <Col className='text-left'>
              {currentPassenger ? (
                <Button color='danger' onClick={onDelete}>
                  {I18n.t('dashboard_delete')}
                </Button>
              ) : (
                <Button color='default' onClick={onCloseModal}>
                  {I18n.t('dashboard_cancel')}
                </Button>
              )}
            </Col>
            <Col className='text-right'>
              {currentPassenger ? (
                <Button color='primary' name='updateOne' onClick={onSubmit}>
                  {I18n.t('dashboard_save')}
                </Button>
              ) : (
                <Button color='primary' name='addOne' onClick={onSubmit}>
                  {I18n.t('dashboard_add')}
                </Button>
              )}
            </Col>
          </Row>
        </Container>
      }
    />
  )
}

export default ModalPassenger
